import { NextPageContext } from 'next';
import { shouldCompleteSignup } from 'lib/redirect';
import { SignUp } from 'features/SignUp';
import { GondolaHead } from 'components/GondolaHead/GondolaHead';
import { FirebaseAuthProvider } from 'lib/contexts/firebaseAuthContext';
import { EmailVerificationService } from 'server/api/services/emailVerification';
import { getCurrentUserFromContext } from 'server/api/services/auth';
import { UserState } from 'lib/types/users';
import { WelcomeUserProvider } from 'lib/contexts/welcomeUserContext';
import User from 'server/models/User';
import { serialize } from 'lib/helpers';

export const getServerSideProps = async (ctx: NextPageContext) => {
  const {
    referredBy, emailVerification, returnTo, welcome,
  } = ctx.query;
  const currentUser = await getCurrentUserFromContext(ctx) as unknown as UserState;
  let verifiedEmail: string | undefined;

  if (currentUser?.id && !shouldCompleteSignup(currentUser)) {
    return {
      redirect: {
        destination: returnTo || '/',
        permanent: false,
      },
    };
  }

  if (emailVerification && !currentUser?.id) {
    const email = await EmailVerificationService
      .asUserId(currentUser?.id)
      .validateLink(emailVerification as string);
    if (email) {
      verifiedEmail = email as string;
    }
  }

  let welcomeUser;
  if (welcome && !currentUser?.id) {
    welcomeUser = await User.query()
      .select('id', 'username', 'name', 'avatarUrl')
      .whereRaw('LOWER(username) = :welcome', { welcome: (welcome as string).toLocaleLowerCase() })
      .whereNot({ termsAcknowledged: true })
      .findOne({});
  }

  return {
    props: {
      ...(referredBy && { referredBy }),
      ...(verifiedEmail && { verifiedEmail }),
      ...(welcomeUser && { welcomeUser: serialize(welcomeUser) }),
    },
  };
};

interface Props {
  referredBy: string;
  verifiedEmail?: string;
  welcomeUser?: User;
}

const SignupPage = ({ referredBy, verifiedEmail, welcomeUser }: Props) => (
  <>
    <GondolaHead
      title="Sign up for Gondola"
      description="Join Gondola today and start tracking your content and connecting with creatives just like you."
      url="https://gondola.cc/signup"
    />
    <FirebaseAuthProvider>
      <WelcomeUserProvider user={welcomeUser}>
        <SignUp referredBy={referredBy} verifiedEmail={verifiedEmail} />
      </WelcomeUserProvider>
    </FirebaseAuthProvider>
  </>
);

// eslint-disable-next-line import/no-default-export
export default SignupPage;
